import React, { useEffect, useState } from 'react';

import { Search, X } from 'lucide-react';
import { queryCache, useMutation, useQuery } from 'react-query';
import { DotBricksLoading, Toast } from 'src/common/components';

import { addCandidateFromCatalog, fetchFeatureCatalog } from '../api';
import { CANDIDATES_QUERY_KEY, FEATURE_NOT_ACTIVE_ADD_ERROR } from '../constants';
import { FeatureCatalogEntry } from '../types';
import { getFeatureFieldErrors } from '../utils';

type CatalogPickerModalProps = {
  editionId: string;
  onClose: () => void;
  onAdded: () => void;
};

const CatalogPickerModal: React.FC<CatalogPickerModalProps> = ({ editionId, onClose, onAdded }) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const { data, isLoading } = useQuery(
    ['featureCatalogSearch', debouncedQuery, editionId],
    () => fetchFeatureCatalog(debouncedQuery, editionId),
    { keepPreviousData: true }
  );

  const entries = data?.data.results ?? [];

  const [addFromCatalog, { isLoading: isAdding }] = useMutation(
    (featureId: number) => addCandidateFromCatalog(editionId, featureId),
    {
      onSuccess: () => {
        void queryCache.invalidateQueries([CANDIDATES_QUERY_KEY, editionId]);
        Toast.success('Candidato agregado del catálogo.');
        onAdded();
        onClose();
      },
      onError: (error) => {
        Toast.error(
          getFeatureFieldErrors(error).feature === 'feature_not_active'
            ? FEATURE_NOT_ACTIVE_ADD_ERROR
            : 'Error al agregar el candidato.'
        );
      },
    }
  );

  return (
    <div className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center">
      <div className="rounded-2xl w-full max-w-lg mx-4 overflow-hidden bg-white shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-base font-bold text-gray-900">Agregar del catálogo</h3>
          <button type="button" className="btn btn-ghost p-1 text-gray-400" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="px-5 pt-4">
          <div className="relative">
            <Search size={15} className="left-3 top-1/2 absolute text-gray-400 -translate-y-1/2" />
            <input
              className="input pl-9 w-full text-sm"
              placeholder="Buscar feature del catálogo…"
              value={query}
              autoFocus
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="max-h-80 px-5 pb-5 mt-3 space-y-2 overflow-y-auto">
          {isLoading && <DotBricksLoading />}
          {!isLoading && entries.length === 0 && (
            <p className="py-6 text-sm text-center text-gray-400">
              {debouncedQuery ? 'Sin resultados para esa búsqueda.' : 'Escribe para buscar…'}
            </p>
          )}
          {entries.map((entry: FeatureCatalogEntry) => (
            <button
              key={entry.id}
              type="button"
              className="rounded-xl hover:border-blue-300 hover:bg-blue-50 w-full p-3 text-left transition-colors border border-gray-200"
              disabled={isAdding}
              onClick={() => addFromCatalog(entry.id)}
            >
              <div className="flex items-center gap-2">
                <span className="flex-1 text-sm font-semibold text-gray-800">{entry.title}</span>
                {entry.source === 'pr_scan' && (
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                    PR scan
                  </span>
                )}
              </div>
              {entry.customer_benefit && (
                <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">
                  {entry.customer_benefit}
                </p>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CatalogPickerModal;
